import { createCache, getValue } from '@glimmer/tracking/primitives/cache'
import type { KeyedRange } from './keys'
import type { DatesEntry, TimesEntry } from './types'
import type { Caches } from './caches'
import type { DatesWeekPair, Scopes } from './scopes'

// Folds partial per-request BE responses into the AvailabilityDate[] shape the UI renders.
// A response's silence about a field means "wasn't asked," never "zero" or "gone."

/**
 * Tags one entry's contribution with which fold consumes it, stamped with the freshness that
 * orders it against every other contribution.
 *
 * Infer the kind from the tag, never from which field happens to be populated — collapsing the two
 * variants back into one shape reintroduces silent cross-family merges.
 */
export type Contribution =
  | { dataUpdatedAt: number; dates: AvailabilityDate[] }
  | { dataUpdatedAt: number; times: AvailabilityTime[] }

function datesContribution(entry: DatesEntry): Contribution {
  return { dataUpdatedAt: entry.dataUpdatedAt, dates: entry.data }
}

function timesContribution(entry: TimesEntry): Contribution {
  return { dataUpdatedAt: entry.dataUpdatedAt, times: entry.data }
}

interface ResourceOptionEntry {
  qty?: number
  pricing?: AvailabilityTimePricing[]
}

/**
 * Keyed by resourceOptionId so an option's qty and pricing land in one entry even when they
 * arrive in separate fetches.
 */
type TimeEntry =
  Partial<Omit<AvailabilityTime, 'id' | 'date' | 'availability' | 'prices'>> &
  {
    id: string
    date: string
    resourceOptions: Map<string, ResourceOptionEntry>
  }

/** Keyed by `AvailabilityTime.id` so upserting one slot never scans the full times array. */
type DateEntry =
  Partial<Omit<AvailabilityDate, 'date' | 'availabilityTimes'>> &
  {
    date: string
    times: Map<string, TimeEntry>
  }

/**
 * The union of every contribution, built fresh on each read and discarded once
 * `projectContributions` converts it.
 *
 * Never hold a reference to one across reads. Nothing outside this module ever sees an
 * Accumulator, which is what lets every level mutate in place instead of copying.
 */
type Accumulator = Map<string, DateEntry>

/**
 * Merges `incoming`'s populated fields onto whatever is stored at `key`, seeding from `base` on
 * first insert, and returns the merged entry so the caller can keep writing into it.
 *
 * Mutates `map` in place. That looks unsafe for a merge, but no caller keeps a reference into a
 * previous accumulator to protect, and copying it here used to block the main thread for roughly
 * a second per ticket click.
 */
function mergeScalars<T extends object>(map: Map<string, T>, key: string, incoming: Partial<T>, base: T): T {
  const merged = { ...(map.get(key) ?? base) } // must copy — entries can be verbatim BE objects, never written through

  for (const field of Object.keys(incoming) as (keyof T)[]) {
    const value = incoming[field]
    if (value !== undefined && value !== null) {
      merged[field] = value as T[keyof T]
    }
  }

  map.set(key, merged)
  return merged
}

/**
 * Writes one field of a resource option's entry, leaving any other field already recorded there
 * untouched.
 */
function mergeResourceOptionField<K extends keyof ResourceOptionEntry>(
  resourceOptions: Map<string, ResourceOptionEntry>,
  resourceOptionId: string,
  field: K,
  value: ResourceOptionEntry[K]
): void {
  const merged = { ...resourceOptions.get(resourceOptionId) }
  if (value !== undefined && value !== null) merged[field] = value
  resourceOptions.set(resourceOptionId, merged)
}

/**
 * Folds one time slot's scalars and its `availability[]`/`prices[]` into `acc`, creating the
 * slot's date entry first if this is the first contribution to land for that date.
 *
 * Fold `availability` and `prices` through separate passes, never a combined one. A times-only
 * refetch can carry qty without pricing or the reverse, and merging them together would let one's
 * absence in a single response erase the other's already-known value.
 */
function foldTime(acc: Accumulator, time: AvailabilityTime): void {
  const { availability, prices, date, ...scalars } = time

  // get-or-create, not merge — a slot carries no date-level scalars to protect, so spreading
  // here would be pure allocation
  let dateEntry = acc.get(date)
  if (!dateEntry) {
    dateEntry = { date, times: new Map() }
    acc.set(date, dateEntry)
  }

  const timeEntry = mergeScalars<TimeEntry>(
    dateEntry.times,
    time.id,
    { ...scalars, date },
    { id: time.id, date, resourceOptions: new Map() }
  )

  for (const entry of availability ?? []) {
    if (entry.resourceOptionId) mergeResourceOptionField(timeEntry.resourceOptions, entry.resourceOptionId, 'qty', entry.qty)
  }

  for (const entry of prices ?? []) {
    if (entry.resourceOptionId) mergeResourceOptionField(timeEntry.resourceOptions, entry.resourceOptionId, 'pricing', entry.pricing)
  }
}

/**
 * Folds one date's own scalars into `acc`, then folds its nested `availabilityTimes` through
 * `foldTime`.
 *
 * A dates response always carries nested times for the whole week it covers, so most slot data
 * arrives through here rather than through a direct times contribution.
 */
function foldDate(acc: Accumulator, date: AvailabilityDate): void {
  const { availabilityTimes, ...scalars } = date

  mergeScalars<DateEntry>(acc, date.date, scalars, { date: date.date, times: new Map() })
  for (const time of availabilityTimes ?? []) foldTime(acc, time)
}

/**
 * Folds every contribution into one accumulator, applied oldest-to-freshest by `dataUpdatedAt` so
 * a later write wins on any field the two disagree about.
 *
 * Sort dates and times together, never one family fully before the other — an older times
 * response applied after a newer dates response would let the stale one win.
 */
function foldContributions(contributions: Contribution[]): Accumulator {
  const ordered = [...contributions].sort((a, b) => a.dataUpdatedAt - b.dataUpdatedAt)

  const acc: Accumulator = new Map()
  for (const contribution of ordered) {
    if ('times' in contribution) {
      for (const time of contribution.times) foldTime(acc, time)
    } else {
      for (const date of contribution.dates) foldDate(acc, date)
    }
  }

  return acc
}

/**
 * Converts each slot's resourceOptions map back into `availability[]`/`prices[]` array pairs.
 *
 * Push a resource option only into the array whose field it actually carries, so a slot that only
 * ever received qty doesn't invent a pricing entry. Always emit both arrays, even empty, since
 * every consumer iterates them unguarded.
 */
function toAvailabilityTimes(times: Map<string, TimeEntry>): AvailabilityTime[] {
  const result: AvailabilityTime[] = []

  for (const timeEntry of times.values()) {
    const { resourceOptions, ...rest } = timeEntry

    const availability: AvailabilityTimeAvailability[] = []
    const prices: AvailabilityTimePrice[] = []

    for (const [resourceOptionId, entry] of resourceOptions) {
      if (entry.qty !== undefined) availability.push({ resourceOptionId, qty: entry.qty })
      if (entry.pricing !== undefined) prices.push({ resourceOptionId, pricing: entry.pricing })
    }

    result.push({ ...rest, availability, prices } as AvailabilityTime)
  }

  return result
}

/**
 * Projects a set of contributions into the `AvailabilityDate[]` shape the UI renders, ordered by
 * date regardless of the input order.
 *
 * Only fields actually populated during the fold are emitted — a full dates+times response
 * folded in and read back out round-trips exactly, but a partial one stays partial.
 */
export function projectContributions(contributions: Contribution[]): AvailabilityDate[] {
  const dates: AvailabilityDate[] = []

  for (const dateEntry of foldContributions(contributions).values()) {
    const { times, ...rest } = dateEntry
    dates.push({ ...rest, availabilityTimes: toAvailabilityTimes(times) } as AvailabilityDate)
  }

  return dates.sort((a, b) => a.date.localeCompare(b.date))
}

/**
 * Builds the two memoized projections over the entry maps: `currentValue` (the UI-shaped,
 * exclusive-per-week pick over the visible scope) and `rangeCapacityDates` (the capacity-shaped
 * fold-both over both dates families).
 *
 * Decide WHICH entries feed a fold here. Freshness and fetching belong to `caches.ts`, not this
 * function.
 */
export function createProjection(
  caches: Caches,
  {
    keyScopes,
    currentDatesPairs,
    currentTimesKeyed
  }: {
    keyScopes: () => Scopes
    currentDatesPairs: () => DatesWeekPair[]
    currentTimesKeyed: () => KeyedRange[]
  }
) {
  const { datesCache, timesCache, usableDates, usableTimes } = caches

  // the last times key that had usable data — this loop's fallback. Untracked, like `lastReadAt`,
  // so writing it mid-projection can't re-dirty the projection itself
  let lastUsableTimesRange: KeyedRange | undefined

  /**
   * Times entries feeding the projection: the selected date's quantity-scoped entry, or that same
   * date's previous quantity-scoped entry while the current one has no usable data yet.
   *
   * The fallback stays on the same date — `foldTime` keys by the entry's own `date` field, so a
   * fallback across a date change shows the wrong day's price under a phantom date.
   */
  function projectableTimesEntries(keyed: KeyedRange[]): TimesEntry[] {
    const contributing: Array<[string, TimesEntry]> = []

    for (const range of keyed) {
      const entry = timesCache.read(range.key)

      if (usableTimes(entry)) {
        contributing.push([range.key, entry])
        lastUsableTimesRange = range
        continue
      }

      const fallback = lastUsableTimesRange
      if (!fallback || fallback.key === range.key || fallback.weekRange !== range.weekRange) continue

      const fallbackEntry = timesCache.read(fallback.key)
      if (usableTimes(fallbackEntry)) contributing.push([fallback.key, fallbackEntry])
    }

    timesCache.touch(contributing.map(([key]) => key))

    return contributing.map(([, entry]) => entry)
  }

  /**
   * Picks one entry for a week: the quantity-scoped entry if it has usable data, else that week's
   * seed, never both.
   */
  function pickDatesEntry(pair: DatesWeekPair): [string, DatesEntry] | undefined {
    const quantityEntry = datesCache.read(pair.quantity.key)
    if (usableDates(quantityEntry)) return [pair.quantity.key, quantityEntry]

    const seedEntry = datesCache.read(pair.seed.key)
    if (usableDates(seedEntry)) return [pair.seed.key, seedEntry]

    return undefined
  }

  function projectableDatesEntries(pairs: DatesWeekPair[]): DatesEntry[] {
    const contributing = pairs.map(pickDatesEntry).filter((pick): pick is [string, DatesEntry] => !!pick)

    datesCache.touch(contributing.map(([key]) => key))

    return contributing.map(([, entry]) => entry)
  }

  /** Folds the entries behind the VISIBLE range. Fetching follows the anchored scope instead. */
  const projection = createCache<AvailabilityDate[]>(() =>
    projectContributions([
      ...projectableDatesEntries(currentDatesPairs()).map(datesContribution),
      ...projectableTimesEntries(currentTimesKeyed()).map(timesContribution)
    ])
  )

  function currentValue(): AvailabilityDate[] {
    caches.scheduleFetches() // must run before the read below, so a render never itself writes tracked state
    return getValue(projection) ?? []
  }

  /**
   * The fold-both union of every entry in the current keyed set that still has usable data: each
   * week's quantity-scoped dates entry AND its seed, plus the selected date's times entry.
   *
   * Fold the quantity entry and its seed together — unlike the scoped projection, which picks one.
   * `stores/availability.ts` derives `getPricesForTicket` and `currency` from here because a
   * sibling ticket type's qty and price live only in the seed, and that exclusive pick drops them
   * the moment any ticket is selected (DEV-9267).
   *
   * Never stamp read warmth and never schedule a fetch here. Every consumer already reads the
   * scoped projection alongside this, which ensures and warms these same keys, so this stays a
   * passive read over whatever is already resident.
   */
  const capacity = createCache<AvailabilityDate[]>(() => {
    const { visible } = keyScopes()

    const contributions: Contribution[] = [
      ...visible.dates.map(({ key }) => datesCache.read(key)).filter(usableDates).map(datesContribution),
      ...visible.times.map(({ key }) => timesCache.read(key)).filter(usableTimes).map(timesContribution)
    ]

    return projectContributions(contributions)
  })

  function rangeCapacityDates(): AvailabilityDate[] {
    return getValue(capacity) ?? []
  }

  return { currentValue, rangeCapacityDates }
}
